// Which suggestions the reviewer has left undecided on the DE2 surface.
//
// findOutstanding is a function, not a value: the form is read with getValues,
// which does not re-render, so the only honest moment to decide is the click.

import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  DE2SuggestibleField,
  de2SuggestibleFields,
} from './de2SuggestibleFields';
import { isDE2FieldEmpty } from './de2FieldEmptiness';
import {
  trackOutstandingSuggestionsModal,
  trackSuggestionDecision,
} from './tracking';
import {
  OutstandingSuggestion,
  findOutstandingSuggestions,
  rejectAllSuggestions,
} from 'concepts/AutoDocExtraction/outstandingSuggestions';
import {
  BULK_REJECT_TRIGGER,
  OutstandingSuggestionsModalStep,
  OutstandingSuggestionsTracking,
} from 'concepts/AutoDocExtraction/outstandingSuggestionsTracking';
import {
  getDE2SuggestionStatusStorageKey,
  SuggestionStatusBucket,
} from 'concepts/AutoDocExtraction/suggestionStatusStorage';
import { useAutoDocExtSuggestionContext } from 'concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import { TemplateBlock } from 'concepts/Extraction/Blocks/BaseBlock';
import { BlockRecords } from 'concepts/Extraction/BlocksData';
import { Factory } from 'concepts/Extraction/Blocks/factory';
import { useAllAutoDocumentExtractions } from 'hooks/study/useAllAutoDocumentExtractions';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import { useDocumentViewerContext } from 'contexts/DocumentViewerProvider';
import usePersistedState from 'hooks/usePersistedState';

export type DE2OutstandingSuggestion = DE2SuggestibleField & {
  suggestion: string;
};

type UseDE2OutstandingSuggestionsArgs = {
  blocks: TemplateBlock[];
  form: UseFormReturn;
};

export type UseDE2OutstandingSuggestionsResult = {
  findOutstanding: () => DE2OutstandingSuggestion[];
  rejectAll: (outstanding: OutstandingSuggestion[]) => void;
  tracking: OutstandingSuggestionsTracking<DE2OutstandingSuggestion>;
};

// An option-backed control renders nothing its options cannot express.
// A block type that does not answer for itself can express anything.
export const canFieldExpressSuggestion = (
  { block }: DE2SuggestibleField,
  suggestion: string
): boolean => Factory(block).canExpressSuggestion?.(suggestion, block) ?? true;

export const useDE2OutstandingSuggestions = ({
  blocks,
  form,
}: UseDE2OutstandingSuggestionsArgs): UseDE2OutstandingSuggestionsResult => {
  const {
    reviewId,
    studyId,
    useSuggestions,
  } = useAutoDocExtSuggestionContext();
  const { selectedDocument } = useDocumentViewerContext();
  const { hasFeature } = useReviewFeatures();

  const documentId = selectedDocument?.id ?? undefined;
  const isDocumentExtractable = selectedDocument?.extractable ?? false;

  const [
    statusBucket,
    setStatusBucket,
  ] = usePersistedState<SuggestionStatusBucket>(
    getDE2SuggestionStatusStorageKey({ reviewId, documentId }),
    {}
  );

  // A request still in flight must never hold up completion.
  const { data: suggestionsByField } = useAllAutoDocumentExtractions({
    enabled: useSuggestions && isDocumentExtractable,
    studyId,
    documentId,
  });

  const findOutstanding = useCallback(() => {
    const values = (form.getValues() ?? {}) as BlockRecords;

    return findOutstandingSuggestions<DE2SuggestibleField>({
      fields: de2SuggestibleFields({ blocks, hasFeature }),
      suggestionsByField: suggestionsByField ?? {},
      statusBucket,
      isFieldEmpty: (field) => isDE2FieldEmpty(field, values),
      isFieldEnabled: canFieldExpressSuggestion,
    });
  }, [blocks, form, hasFeature, statusBucket, suggestionsByField]);

  const rejectAll = useCallback(
    (outstanding: OutstandingSuggestion[]) =>
      rejectAllSuggestions(setStatusBucket, outstanding),
    [setStatusBucket]
  );

  const tracking = useMemo<
    OutstandingSuggestionsTracking<DE2OutstandingSuggestion>
  >(() => {
    const context = {
      review_id: reviewId,
      study_id: studyId,
      document_id: documentId ?? null,
    };
    const report = (step: OutstandingSuggestionsModalStep) => (
      outstanding: DE2OutstandingSuggestion[]
    ) => trackOutstandingSuggestionsModal(step, outstanding, context);

    // Each rejected field also reports the decision every individual
    // accept/reject reports, so both kinds land in the one report.
    const reportRejections = (outstanding: DE2OutstandingSuggestion[]) =>
      outstanding.forEach(({ autoDocExtField, block, suggestion }) =>
        trackSuggestionDecision('rejected', {
          ...context,
          field: autoDocExtField,
          field_alias: block.label,
          suggestion,
          trigger: BULK_REJECT_TRIGGER,
        })
      );

    return {
      modalViewed: report('viewed'),
      modalDismissed: report('dismissed'),
      modalRejectAll: (outstanding: DE2OutstandingSuggestion[]) => {
        report('reject_all')(outstanding);
        reportRejections(outstanding);
      },
      modalReview: report('review'),
    };
  }, [reviewId, studyId, documentId]);

  return { findOutstanding, rejectAll, tracking };
};
